import {
  Avatar,
  Box,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
} from '@mui/material';
import {
  DateView,
  DesktopDatePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { slashDateFormat } from 'Config';
import { he } from 'date-fns/locale';
import { useIsDesktop } from 'Hooks';
import {
  AccordionArrowBlue,
  PurpleDateIcon,
  LeftArrowDatePicker,
  RightArrowDatePicker,
} from 'Images';

interface IDatePicker {
  value: Date | null;
  label: string;
  maxDate: Date;
  minDate?: Date;
  onChange: (value: Date | null) => void;
  maxWidth?: string;
  views?: DateView[];
  sx?: SxProps<Theme>;
  OpenPickerIcon?: React.ElementType<any>;
  renderInput?: (props: TextFieldProps) => React.ReactElement;
  placeholder?: string;
}

export const DatePicker = ({
  value,
  label,
  maxDate,
  minDate,
  onChange,
  maxWidth,
  views,
  sx,
  OpenPickerIcon,
  renderInput,
  placeholder,
}: IDatePicker) => {
  const isDesktop = useIsDesktop();

  const isYearMonthOnly =
    views &&
    views.length === 2 &&
    views.includes('year') &&
    views.includes('month');

  const getInputFormat = () => {
    if (isYearMonthOnly) {
      return 'MM/yyyy';
    }
    return slashDateFormat;
  };

  const handleDateChange = (newValue: Date | null) => {
    if (newValue && isYearMonthOnly) {
      const firstOfMonth = new Date(
        newValue.getFullYear(),
        newValue.getMonth(),
        1,
      );
      onChange(firstOfMonth);
    } else {
      onChange(newValue);
    }
  };

  return (
    <Box
      sx={{
        width: !isDesktop || maxWidth === '100%' ? '100%' : undefined,
        ...(placeholder
          ? {}
          : {
              '.MuiFormLabel-root': {
                left: 'auto',
                right: '24px',
                top: '-4px',
              },
              '.MuiFormLabel-root[data-shrink="true"]': {
                right: '2px',
              },
              '.MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
            }),
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
        <DesktopDatePicker
          enableAccessibleFieldDOMStructure={false}
          views={views ?? undefined}
          label={placeholder ? undefined : label}
          format={getInputFormat()}
          value={value}
          onChange={handleDateChange}
          slotProps={{
            popper: {
              disablePortal: isDesktop ? false : true,
            },
            textField: renderInput
              ? undefined
              : {
                  placeholder,
                  inputProps: {
                    readOnly: true,
                    'aria-label': label,
                    style: { cursor: isYearMonthOnly ? 'text' : 'pointer' },
                  },
                  sx: [
                    {
                      '.MuiOutlinedInput-input': {
                        textAlign: 'right',
                        cursor: isYearMonthOnly ? 'text' : 'pointer',
                      },
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.shade_200',
                      },
                      '.MuiInputLabel-root': {
                        fontWeight: 400,
                        fontSize: '18px',
                      },
                      '.MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        cursor: isYearMonthOnly ? 'text' : 'pointer',
                        borderRadius: placeholder ? '8px' : '6px',
                        height: '48px',
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                      width: '100%',
                      maxWidth: maxWidth
                        ? maxWidth
                        : isDesktop
                          ? '221px'
                          : '100%',
                      flexGrow: 1,
                      '& label[data-shrink="true"]': {
                        mt: isDesktop ? '3px' : '5px',
                      },
                      ...(placeholder
                        ? {
                            '& input::placeholder': {
                              color: '#656F8B',
                              opacity: 1,
                              fontSize: '20px',
                              lineHeight: '26px',
                            },
                            '.MuiOutlinedInput-input': {
                              textAlign: 'right',
                              cursor: isYearMonthOnly ? 'text' : 'pointer',
                              fontSize: '20px',
                              lineHeight: '26px',
                            },
                          }
                        : {}),
                    },
                    ...(sx ? [sx] : []),
                  ] as SxProps<Theme>,
                },
            desktopPaper: {
              sx: {
                borderRadius: '6px',
                width: isDesktop ? '400px' : undefined,
                ml: isDesktop ? '65px' : '27px',
                '.PrivatePickersFadeTransitionGroup-root, div[aria-live="polite"]':
                  {
                    fontSize: isDesktop ? '1.375rem' : '1.125rem',
                    fontWeight: 400,
                    lineHeight: isDesktop ? '1.75rem' : '1.5rem',
                    color: '#1752AF',
                  },
                '.MuiTypography-caption': {
                  fontSize: isDesktop ? '1.125rem' : '1rem',
                  fontWeight: 700,
                  lineHeight: isDesktop ? '1.5rem' : '1.25rem',
                  color: '#39546A',
                },
                '.MuiPickersDay-root': {
                  fontSize: isDesktop ? '1.125rem' : '1rem',
                  fontWeight: 400,
                  lineHeight: isDesktop ? '1.625rem' : '1.375rem',
                },
                '.PrivatePickersYear-root > button': {
                  fontSize: isDesktop ? '1.125rem' : '1rem',
                  fontWeight: 400,
                  lineHeight: isDesktop ? '1.625rem' : '1.375rem',
                },
                '.MuiPickersDay-today:not(.Mui-selected)': {
                  border: 'none !important',
                  backgroundColor: 'white !important',
                },
                '.Mui-selected': {
                  color: 'white',
                },
              },
            },
          }}
          maxDate={maxDate}
          minDate={minDate}
          slots={{
            openPickerIcon:
              OpenPickerIcon ||
              ((props) => (
                <Avatar
                  {...props}
                  src={PurpleDateIcon}
                  alt=''
                  sx={{ width: '32px', height: '32px' }}
                />
              )),
            leftArrowIcon: (props) => (
              <Avatar
                {...props}
                src={LeftArrowDatePicker}
                alt=''
                sx={{ width: '32px', height: '32px' }}
              />
            ),
            rightArrowIcon: (props) => (
              <Avatar
                {...props}
                src={RightArrowDatePicker}
                alt=''
                sx={{ width: '32px', height: '32px' }}
              />
            ),
            switchViewIcon: (props) => (
              <Avatar
                {...props}
                src={AccordionArrowBlue}
                alt=''
                sx={{ width: '28px', height: '28px' }}
              />
            ),
            textField: renderInput
              ? renderInput
              : (props) => <TextField {...props} />,
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};
